/**
 * "Eleven are new" is not an answer when there are a hundred and five cards.
 *
 * The badge on the tab says how much arrived; this says WHERE, and gives you
 * the two things you can do about it: walk them, or let them go.
 *
 * IT STAYS PUT WHILE THE REPORT SCROLLS UNDER IT. The button is how you walk
 * the list — press, read, press again — and one that scrolls away with the
 * first row it took you to is a button you use once and then hunt for. Sticky
 * to the top of the box that scrolls, so "down, down, down" is the same click
 * each time, and the count on it falls as the rows behind it are read.
 *
 * So A REPORT IS NOT A SCROLL BOX OF ITS OWN. Sticky holds to the nearest box
 * that could scroll; a report that was one, inside the open table's box that
 * really scrolls, grew to its full height and took this up with it.
 *
 * CLEAR IS NOT A SHORTCUT FOR ↓. Twelve rows in a table of forty are worth
 * walking; twelve hundred after a long dig are not, and without this the only
 * way down was to scroll past every one of them. It reads what is waiting HERE
 * — this report, matching the count beside it — and never the whole room.
 *
 * ONE PER REPORT, not one per table: a report draws a table per solution type
 * or per axis, and a button above each of them could not say which rows it
 * meant. Renders nothing once nothing is lit — not when the last one is READ,
 * which left rows still fading with no "clear all" to put them out.
 */
export default function NewHere({
  count,
  glowing = count,
  onJump,
  onClearAll,
}: {
  count: number;
  /** Rows still lit, read or not. The pill stays until this reaches zero. */
  glowing?: number;
  onJump: () => void;
  /** Read them all where they are. Absent on a table that is not a report. */
  onClearAll?: () => void;
}) {
  if (!count && !glowing) return null;
  return (
    <div className="sticky top-0 z-20 shrink-0 self-start -mb-1 pb-1">
      <div className="inline-flex items-center rounded-full border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/90 text-xs shadow-sm backdrop-blur overflow-hidden">
        <button
          type="button"
          onClick={onJump}
          disabled={!count}
          title={count ? 'Scroll to the next one that arrived since you last looked' : 'All read - the last ones are fading out'}
          className="inline-flex items-center gap-1 px-2 py-0.5 text-amber-900 dark:text-amber-200 hover:bg-amber-100 dark:hover:bg-amber-900 disabled:hover:bg-transparent disabled:cursor-default"
          data-testid="new-here"
        >
          {count ? (
            <>
              <span className="tabular-nums">{count}</span> new here
              <span aria-hidden>↓</span>
            </>
          ) : (
            'all read'
          )}
        </button>
        {onClearAll && (
          <button
            type="button"
            onClick={onClearAll}
            title={count ? `Mark all ${count} as read without visiting them` : 'Put out the glow now'}
            className="px-2 py-0.5 border-l border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 hover:bg-amber-100 dark:hover:bg-amber-900"
            data-testid="new-here-clear"
          >
            clear all
          </button>
        )}
      </div>
    </div>
  );
}
